import type { Editor } from "@tiptap/react";
import type { ChatMessage } from "schemas/generated/chatMessage";
import type { ScrollToBottom } from "use-stick-to-bottom";
import type { MessageInputRef } from "./chat-elements/message-input/MessageInput";

import React, { forwardRef, useCallback, useLayoutEffect, useRef } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { MessageInput } from "@v2/features/chat/components/chat-elements/message-input/MessageInput";
import MessageLimitWrapper from "@v2/features/chat/components/chat-elements/message-input/MessageLimitWrapper";
import { MessageList } from "@v2/features/chat/components/chat-elements/MessageList";
import { ChatEditorContext } from "@v2/features/chat/components/ReactEditorChat";
import { useQuickPrompt } from "@v2/features/chat/hooks/useQuickPrompt";
import { useCurrentChatContext } from "@v2/features/chat/providers/CurrentChatContext";
import {
  useIsChatLoading,
  useMessagesForChat,
} from "@v2/features/chat/store/ChatStoreContext";
import Modifiers from "@v2/features/modifiers/Modifiers";
import {
  useDisplayMode,
  useIsAnyElementSelected,
} from "@v2/features/modifiers/store/ModifiersStoreContext";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useReploHotkeys } from "@v2/hooks/useHotkeys";
import { ChevronDown } from "lucide-react";
import { motion } from "motion/react";
import { useSearchParams } from "react-router-dom";
import useMeasure from "react-use-measure";
import { useRequiredContext } from "replo-utils/react/context";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";

export const ChatMessageListContext = React.createContext<{
  messages: ChatMessage[];
}>({
  messages: [],
});

const ChatContainer = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={twMerge(
        "grid max-h-full w-full grid-rows-[1fr_auto] h-full overflow-hidden min-h-0",
        className,
      )}
      {...props}
    />
  );
});
ChatContainer.displayName = "ChatContainer";

// NOTE (Ben, 2025-12-05): Captures scrollToBottom from StickToBottom's context into a ref.
// This allows us to call scrollToBottom from outside the StickToBottom tree (e.g., in handleSubmitWithScroll).
const ScrollToBottomCapture = ({
  scrollToBottomRef,
}: {
  scrollToBottomRef: React.MutableRefObject<ScrollToBottom | null>;
}) => {
  const { scrollToBottom } = useStickToBottomContext();

  useLayoutEffect(() => {
    scrollToBottomRef.current = scrollToBottom;
  }, [scrollToBottomRef, scrollToBottom]);

  return null;
};

const ChatScrollButton = () => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  if (isAtBottom) {
    return null;
  }

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-in fade-in duration-200">
      <Button
        icon
        variant="secondary"
        onClick={() => void scrollToBottom()}
        aria-label="Scroll to bottom"
        UNSAFE_className="group w-6 h-6 min-w-6 min-h-6 rounded-full p-0 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
      >
        <ChevronDown className="w-3 h-3 text-muted-foreground transition-colors duration-200 group-hover:text-foreground" />
      </Button>
    </div>
  );
};

// NOTE (Ben, 2025-12-12): Custom infinite scroll component for inverse (scroll-to-top) loading.
// We can't use react-infinite-scroll-component's inverse mode because its isElementAtTop()
// function has a broken formula that never returns true when there's scrollable content.
const ChatInfiniteScroll = ({
  dataLength,
  next,
  hasMore,
  children,
}: React.PropsWithChildren<{
  dataLength: number;
  next: () => void;
  hasMore: boolean;
}>) => {
  const { scrollRef } = useStickToBottomContext();
  const [isLoading, setIsLoading] = React.useState(false);
  const lastDataLengthRef = React.useRef(dataLength);
  const scrollHeightBeforeLoadRef = React.useRef<number | null>(null);

  // NOTE (Ben, 2025-12-12): Preserve scroll position when new data is prepended
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    if (
      dataLength !== lastDataLengthRef.current &&
      scrollHeightBeforeLoadRef.current !== null
    ) {
      // NOTE (Ben, 2025-12-12): Calculate how much content was added and adjust scroll position
      const scrollHeightDiff =
        el.scrollHeight - scrollHeightBeforeLoadRef.current;
      el.scrollTop = el.scrollTop + scrollHeightDiff;

      setIsLoading(false);
      scrollHeightBeforeLoadRef.current = null;
      lastDataLengthRef.current = dataLength;
    }
  }, [dataLength, scrollRef]);

  // NOTE (Ben, 2025-12-18): Auto-load more if content doesn't fill the container
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    if (el.scrollHeight <= el.clientHeight && hasMore && !isLoading) {
      scrollHeightBeforeLoadRef.current = el.scrollHeight;
      setIsLoading(true);
      next();
    }
  }, [scrollRef, hasMore, isLoading, next]);

  // NOTE (Ben, 2025-12-12): Custom scroll detection for loading more at the top
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const handleScroll = () => {
      const isNearTop = el.scrollTop < 100;

      if (isNearTop && hasMore && !isLoading) {
        // NOTE (Ben, 2025-12-12): Save scroll height before loading so we can restore position after
        scrollHeightBeforeLoadRef.current = el.scrollHeight;
        setIsLoading(true);
        next();
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [scrollRef, hasMore, isLoading, next]);

  return (
    <div
      className={twMerge(
        "flex flex-col-reverse",
        dataLength === 0 && "h-full justify-center",
      )}
    >
      {children}
      {isLoading && hasMore && (
        <div className="flex items-center justify-center py-2">
          <Spinner />
        </div>
      )}
    </div>
  );
};

interface ChatProps {
  handleSubmit: (opts: {
    chatEditor: Editor;
    event: React.FormEvent | null;
  }) => void;
  className?: string;
  stop?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  // NOTE (Ben, 2025-12-05): Ref populated with handleSubmitWithScroll so parent components
  // can trigger submit with scroll behavior. Needed because Enter key submissions go through
  // ChatEditorProvider.onSubmit which bypasses this component's form onSubmit handler.
  onSubmitRef?: React.MutableRefObject<
    | ((opts: { chatEditor: Editor; event: React.FormEvent | null }) => void)
    | null
  >;
}

export function Chat({
  handleSubmit,
  stop,
  className,
  hasNextPage,
  fetchNextPage,
  onSubmitRef,
}: ChatProps) {
  const { editor } = useRequiredContext(ChatEditorContext);
  const messageInputRef = useRef<MessageInputRef>(null);
  const scrollToBottomRef = useRef<ScrollToBottom | null>(null);
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const isChatLoading = useIsChatLoading(chatId);
  const messages = useMessagesForChat(chatId);
  const { chat, files, setFiles } = useCurrentChatContext();
  const ownerId = chat?.userId;

  const lastMessage = messages.at(-1);
  const isTyping = lastMessage?.role === "user" && isChatLoading;

  // NOTE (Ben, 2025-11-04): If the chat has no owner (i.e. no chat created yet), or the owner is the current user, or the current user is a superuser, then they can edit the chat.
  // Otherwise, we hide the chat input.
  const canEdit = !ownerId || ownerId === user?.id || user?.isSuperuser;

  const displayMode = useDisplayMode();
  const isAnyElementSelected = useIsAnyElementSelected();
  const [modifiersContainerRef, { height: modifiersMaxHeight }] = useMeasure();

  const handleQuickPrompt = useQuickPrompt({
    editor,
    onSelectUploadImage: () => {
      void messageInputRef.current?.triggerFileUpload();
    },
    onSelectProduct: () => {
      void messageInputRef.current?.triggerContextMenu();
    },
  });

  // NOTE (Ben, 2025-12-05): Wrapper that triggers scroll to bottom after submitting a message.
  const handleSubmitWithScrollToBottom = useCallback(
    (opts: { chatEditor: Editor; event: React.FormEvent | null }) => {
      // Stop voice recording if active
      messageInputRef.current?.stopVoiceRecording();
      handleSubmit(opts);
      requestAnimationFrame(() => {
        void scrollToBottomRef.current?.({ animation: "smooth" });
      });
    },
    [handleSubmit],
  );

  // NOTE (Ben, 2025-12-05): Expose the scroll-enabled submit handler to parent components
  useLayoutEffect(() => {
    if (onSubmitRef) {
      onSubmitRef.current = handleSubmitWithScrollToBottom;
    }
  }, [onSubmitRef, handleSubmitWithScrollToBottom]);

  useReploHotkeys({
    toggleVoice: () => {
      messageInputRef.current?.toggleVoiceRecording();
    },
  });

  return (
    <ChatMessageListContext.Provider value={{ messages }}>
      <ChatContainer className={twMerge("h-full", className)}>
        <div className="relative flex min-h-0 h-full w-full">
          <StickToBottom
            className="relative flex-1 h-full max-h-full min-h-0 no-scrollbar grid"
            initial="instant"
            resize="instant"
            role="log"
          >
            <ScrollToBottomCapture scrollToBottomRef={scrollToBottomRef} />
            <StickToBottom.Content
              className={twMerge(
                "flex flex-col py-3",
                // NOTE (Ben, 2025-12-09): If there are no messages, set the height to full so we center the placeholder properly,
                // otherwise do not have h-full so ScrollToBottom can work properly.
                messages.length === 0 && "h-full",
              )}
            >
              <ChatInfiniteScroll
                dataLength={messages.length}
                next={fetchNextPage}
                hasMore={hasNextPage}
              >
                <MessageList
                  isTyping={isTyping}
                  editor={editor}
                  onQuickPrompt={handleQuickPrompt}
                />
              </ChatInfiniteScroll>
            </StickToBottom.Content>
            <ChatScrollButton />
          </StickToBottom>

          {/* Chat gradient - creates fade effect at the top into content area */}
          <div className="absolute inset-x-0 top-0 pointer-events-none">
            <div className="h-3 w-full bg-gradient-to-b from-white via-white/90 to-transparent" />
          </div>
          {/* Chat gradient - creates fade effect into input area */}
          <div className="absolute inset-x-0 bottom-0 pointer-events-none">
            <div className="h-3 w-full bg-gradient-to-t from-white via-white/90 to-transparent" />
          </div>

          {canEdit && displayMode === "select" && (
            <div
              ref={modifiersContainerRef}
              className="absolute bottom-2 left-0 right-0 top-2 flex items-end pointer-events-none"
            >
              <div className="flex flex-col pointer-events-auto max-h-full max-w-full w-full">
                {isAnyElementSelected && (
                  <motion.div
                    key="modifiers"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.2, ease: "easeOut" },
                    }}
                    className="flex flex-col"
                  >
                    <Modifiers maxHeight={modifiersMaxHeight} />
                  </motion.div>
                )}
              </div>
            </div>
          )}
        </div>
        {canEdit && (
          <div className="relative min-w-0">
            <form
              onSubmit={(e) => {
                if (!editor) {
                  return;
                }
                handleSubmitWithScrollToBottom({
                  chatEditor: editor,
                  event: e,
                });
              }}
            >
              <div className="relative">
                <MessageLimitWrapper>
                  <MessageInput
                    ref={messageInputRef}
                    files={files}
                    setFiles={setFiles}
                    allowAttachments={true}
                    stop={stop}
                    enableInterrupt={true}
                    editor={editor}
                    isChatLoading={isChatLoading}
                    editorClassName="max-h-[120px]"
                  />
                </MessageLimitWrapper>
              </div>
            </form>
          </div>
        )}
      </ChatContainer>
    </ChatMessageListContext.Provider>
  );
}
